<ng-container *ngIf="!loading; else stillLoading">
  <!-- Form Header -->
  @if (!isErrorStatus && (isGivelink)) {
    <app-donation-form-header
      variant="givelink"
      [fundName]="fundNameForDisplay"
      [isLoading]="!paymentData"
    />
  }

  <!-- Stepper -->
  @if (!isErrorStatus) {
    <div class="stepper-container">
      <app-stepper [steps]="steps" [activeStepIndex]="currentStep"> </app-stepper>
    </div>
  }

  <!-- Main Content -->
  <div
    class="post-payment"
    [class.tol]="environmentName === environments.TOL"
    [class.post-payment--error]="isErrorStatus"
  >
    <div class="post-payment__content">
      <!-- Bordered container only for success/pending states -->
      @if (
        ![paymentStatus.FAILED, paymentStatus.CANCELED, paymentStatus.INSUFFICIENT_AMOUNT].includes(
          paymentData?.status
        )
      ) {
        <div class="post-payment__container">
          <!-- Success Messages -->
          @if (
            [paymentStatus.PENDING, paymentStatus.PURCHASED, paymentStatus.ACTIVE].includes(
              paymentData.status
            )
          ) {
            <!-- Contribution Type Handling -->
            @if (
              paymentData.contributionType === contributionTypes.BANK_TRANSFER_ONLINE ||
              paymentData.contributionType === contributionTypes.CREDIT_CARD ||
              paymentData.contributionType === contributionTypes.SEPA_DEBIT
            ) {
              <!-- Online payment success message -->
              @if (!loadingCountryDetails) {
                @if (usesNewCreditCardDesign()) {
                  <!-- New Credit Card Confirmation Design -->
                  <ng-container *ngTemplateOutlet="newCreditCardConfirmation"></ng-container>
                } @else if (
                  paymentData.taxDeduction &&
                  (networkMemberRedirectUrl || networkMemberSuccessMessage)
                ) {
                  <!-- Network member message for online payments -->
                  <ng-container *ngTemplateOutlet="networkMember"></ng-container>
                } @else {
                  <!-- Standard purchase message -->
                  <ng-container *ngTemplateOutlet="purchaseMessage"></ng-container>
                }
              } @else {
                <app-loading-overlay [visible]="true" [indeterminate]="true"></app-loading-overlay>
              }
            } @else if (paymentData.contributionType === contributionTypes.DONOR_ADVISED_FUND) {
              <ng-container *ngTemplateOutlet="DafMessage"></ng-container>
            } @else if (paymentData.contributionType === contributionTypes.BANK_TRANSFER_OFFLINE) {
              <ng-container *ngTemplateOutlet="BTOfflineMessage"></ng-container>
            } @else if (paymentData.contributionType === contributionTypes.PHYSICAL_CHECK) {
              <ng-container *ngTemplateOutlet="physicalCheckMessage"></ng-container>
            } @else if (
              paymentData.contributionType === contributionTypes.PUBLICLY_TRADED_SECURITIES
            ) {
              <ng-container *ngTemplateOutlet="PTSMessage"></ng-container>
            } @else if (paymentData.contributionType === contributionTypes.OTHER_ASSETS) {
              <ng-container *ngTemplateOutlet="otherMessage"></ng-container>
            }
          }
        </div>
      }

      <!-- Error content rendered directly — no outer border -->
      @if (
        [paymentStatus.FAILED, paymentStatus.CANCELED, paymentStatus.INSUFFICIENT_AMOUNT].includes(
          paymentData?.status
        )
      ) {
        <div class="post-payment__error-content" role="alert" aria-live="assertive">
          <h1 class="post-payment__error-title">
            @if (paymentData.status === paymentStatus.CANCELED) {
              {{ 'contribute.confirmation.error.canceled.title' | translateLang }}
            } @else {
              {{ 'contribute.confirmation.error.title' | translateLang }}
            }
          </h1>
          <div class="post-payment__error-message">
            @if (paymentData.status === paymentStatus.FAILED) {
              <span
                [innerHTML]="
                  isPlainErrorMessage(paymentData.error)
                    ? (paymentData.error | translateLang)
                    : ((paymentData.error | snakeCaseToDotted | translateLang) ||
                        'summary.status.failed' | translateLang)
                "
              ></span>
            }
            @if (paymentData.status === paymentStatus.CANCELED) {
              <span
                [innerHTML]="
                  isPlainErrorMessage(paymentData.error)
                    ? (paymentData.error | translateLang)
                    : ((paymentData.error | snakeCaseToDotted | translateLang) ||
                        'summary.status.canceled' | translateLang)
                "
              ></span>
            }
            @if (paymentData.status === paymentStatus.INSUFFICIENT_AMOUNT) {
              <span>
                {{
                  'summary.status.insufficient.amount'
                    | translateLang
                      : {
                          amount: paymentData.amount | number: '1.2-2' | numberFormat,
                          balance: paymentData.balance | number: '1.2-2' | numberFormat,
                        }
                }}
              </span>
            }
          </div>
          @if (!hasSpecificError(paymentData.error)) {
          <div class="post-payment__error-suggestions">
            <p class="post-payment__error-suggestions-heading">
              {{ 'contribute.confirmation.error.suggestions_heading' | translateLang }}
            </p>
            <div class="post-payment__error-suggestions-list">
              @if (paymentData.status === paymentStatus.CANCELED) {
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{
                      'contribute.confirmation.error.canceled.suggestion.verify_details'
                        | translateLang
                    }}
                  </p>
                </div>
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{
                      'contribute.confirmation.error.canceled.suggestion.start_again'
                        | translateLang
                    }}
                  </p>
                </div>
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{
                      'contribute.confirmation.error.canceled.suggestion.stable_connection'
                        | translateLang
                    }}
                  </p>
                </div>
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{
                      'contribute.confirmation.error.canceled.suggestion.contact_support'
                        | translateLang
                    }}
                  </p>
                </div>
              } @else {
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{
                      'contribute.confirmation.error.suggestion.check_connection' | translateLang
                    }}
                  </p>
                </div>
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>{{ 'contribute.confirmation.error.suggestion.try_again' | translateLang }}</p>
                </div>
                <div class="post-payment__error-suggestion-item">
                  <span class="post-payment__error-bullet">•</span>
                  <p>
                    {{ 'contribute.confirmation.error.suggestion.contact_support' | translateLang }}
                  </p>
                </div>
              }
            </div>
          </div>
          }
        </div>
      }
    </div>

    <!-- Step Counter -->
    @if (!isErrorStatus) {
      <div class="step-counter">
        <span>{{
          'contribute.step_counter'
            | translateLang: { current: currentStep + 1, total: steps.length }
        }}</span>
      </div>
    }

    <!-- Action Buttons -->
    <div class="post-payment__actions">
      <button
        type="button"
        class="post-payment__button post-payment__button--primary"
        (click)="goTo(url.DONATE)"
      >
        {{ 'summary.another.donation' | translateLang }}
      </button>

      @if (public && !paymentData.wasLoggedIn) {
        <button
          type="button"
          class="post-payment__button post-payment__button--secondary"
          (click)="goHome()"
        >
          @if (canceled) {
            {{ 'common.done' | translateLang }}
          } @else {
            <span class="post-payment__button-text--desktop">{{
              'summary.back.to.homepage' | translateLang
            }}</span>
            <span class="post-payment__button-text--mobile">{{
              'summary.back.to.homepage.mobile' | translateLang
            }}</span>
          }
        </button>
      }

      @if (!public || paymentData.wasLoggedIn) {
        <button
          type="button"
          class="post-payment__button post-payment__button--secondary"
          (click)="goToDashboard()"
        >
          @if (canceled) {
            {{ 'common.done' | translateLang }}
          } @else {
            {{ 'contribute.confirmation.back_to_dashboard' | translateLang }}
          }
        </button>
      }
      <!-- @else if (paymentData.wasLoggedIn) {
      <button
        type="button"
        class="post-payment__button post-payment__button--secondary"
        (click)="goTo(url.ACTIVITY)"
      >
        {{ 'summary.back.to.activity' | translateLang }}
      </button>
      } -->
    </div>
  </div>
</ng-container>

<!-- TEMPLATES -->

<!-- Purchase Message Template (Online Payments) -->
<ng-template #purchaseMessage>
  @if (
    paymentData.taxDeduction &&
    environmentName !== environments.TOL &&
    paymentData.networkMemberText
  ) {
    <ng-container *ngTemplateOutlet="networkMember"></ng-container>
  } @else {
    <!-- Success Alert -->
    <div class="post-payment__success-message">
      @if (environmentName !== environments.CNCF || paymentData.recurrencePattern) {
        <svg
          class="post-payment__success-icon"
          width="20"
          height="20"
          viewBox="0 0 20 20"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
          aria-hidden="true"
        >
          <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2" fill="none" />
          <path
            d="M6 10l2.5 2.5L14 7.5"
            stroke="currentColor"
            stroke-width="2"
            stroke-linecap="round"
            stroke-linejoin="round"
          />
        </svg>
        <span class="post-payment__success-text">
          {{
            (environmentName === environments.SINNGEBER && paymentData.recurrencePattern
              ? 'recurring.success'
              : 'general.success'
            ) | translateLang
          }}!
        </span>
        {{ environmentName === environments.SINNGEBER && paymentData.recurrencePattern ? '' : '-' }}
      }
      @if (paymentData.recurrencePattern) {
        {{ 'summary.recurring.contribution.submitted' | translateLang | translateCut: 0 }}
        @if (!public) {
          {{ 'summary.recurring.contribution.submitted' | translateLang | translateCut: 1 }}
          <a class="pointer" (click)="goTo(url.MANAGE_RECURRING)">{{
            'summary.recurring.contribution.submitted' | translateLang | translateCut: 2
          }}</a>
          {{ 'summary.recurring.contribution.submitted' | translateLang | translateCut: 3 }}
        }
      } @else {
        @if (environmentName === environments.CNCF) {
          <span
            [innerHTML]="
              (paymentData.contributionType === contributionTypes.BANK_TRANSFER_ONLINE
                ? 'summary.contribution.submitted.bt.online'
                : 'summary.contribution.submitted.credit.card'
              ) | translateLang: { email: email }
            "
          ></span>
        } @else {
          <span
            [innerHTML]="
              (environments.SINNGEBER &&
              paymentData.contributionType === contributionTypes.SEPA_DEBIT
                ? 'summary.contribution.submitted.sepa'
                : 'summary.contribution.submitted'
              ) | translateLang: { email: email }
            "
          ></span>
        }
      }
      @if (environmentName === environments.CNCF && !paymentData.recurrencePattern) {
        @if (paymentData.contributionType === contributionTypes.BANK_TRANSFER_ONLINE) {
          <span class="font-weight-bold">{{ 'general.next' | translateLang | uppercase }}:</span>
        }
        @if (paymentData.contributionType === contributionTypes.CREDIT_CARD) {
          <svg
            class="post-payment__success-icon"
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="10" cy="10" r="9" stroke="currentColor" stroke-width="2" fill="none" />
            <path
              d="M6 10l2.5 2.5L14 7.5"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          <span class="font-weight-bold">&nbsp;{{ 'general.success' | translateLang }}!</span>
        }
      }
    </div>

    <!-- Summary Details -->
    @if (displaySummaryPage()) {
      <div class="post-payment__details">
        <h4 class="post-payment__section-heading">{{ 'summary.title' | translateLang }}</h4>

        <div class="post-payment__row">
          <span class="post-payment__label">{{ 'general.fund' | translateLang }}</span>
          <span class="post-payment__value">
            {{
              paymentData.recurrencePattern
                ? paymentData.fundName || paymentData.entityName
                : paymentData.entityName
            }}
          </span>
        </div>

        <div class="post-payment__row">
          <span class="post-payment__label">{{ 'summary.amount' | translateLang }}</span>
          <span class="post-payment__value">
            {{ paymentData.currencySymbol }}
            {{ paymentData.amount | number: '1.2-2' | numberFormat }}
          </span>
        </div>

        @if (paymentData.paymentId && !paymentData.recurrencePattern) {
          <div class="post-payment__row">
            <span class="post-payment__label">{{ 'summary.payment.id' | translateLang }}</span>
            <span class="post-payment__value">{{ paymentData.paymentId }}</span>
          </div>
        }
        @if (paymentData.subscriptionId && paymentData.recurrencePattern) {
          <div class="post-payment__row">
            <span class="post-payment__label">{{ 'summary.subscription.id' | translateLang }}</span>
            <span class="post-payment__value">{{ paymentData.subscriptionId }}</span>
          </div>
        }
      </div>
    }
  }
</ng-template>

<!-- Bank Transfer Offline Message Template -->
<ng-template #BTOfflineMessage>
  @if (isAusgivingContribution) {
    <ng-container *ngTemplateOutlet="ausgivingMessage"></ng-container>
  } @else if (
    (paymentData.taxDeduction && environmentName !== environments.SWISS) ||
    (paymentData.taxDeduction &&
      environmentName === environments.SWISS &&
      paymentData.networkMemberLogoUrl)
  ) {
    <ng-container *ngTemplateOutlet="networkMember"></ng-container>
  } @else {
    <div class="post-payment__bt-offline-content">
      @if (environmentName !== environments.CNCF) {
        <div class="post-payment__text-content">
          <p class="post-payment__text">
            {{ 'contribute.confirmation.bank_transfer.instructions_short' | translateLang }}
          </p>
          <p class="post-payment__text">
            {{ 'contribute.confirmation.bank_transfer.email_sent' | translateLang }}
          </p>
        </div>
        <h4 class="post-payment__section-heading">
          {{ 'contribute.confirmation.donation_details' | translateLang }}
        </h4>
        <div class="post-payment__wiring-section">
          <app-wiring-instructions
            [wiringInstructions]="wiringInstructions"
            [showSupplementary]="paymentData.taxDeduction === false"
          >
          </app-wiring-instructions>
        </div>
        @if (environmentName === environments.SINNGEBER) {
          <div class="post-payment__charity-details">
            <span class="post-payment__charity-details-label"
              >{{ 'charity.details' | translateLang }}:
            </span>
            {{ paymentData.entityName + ' (' + paymentData.fundNumber + ')' }}
          </div>
        }
      }
    </div>
  }
</ng-template>

<!-- AusGiving Message Template (Australia, tax deductible, amount < AUD 100,000) -->
<ng-template #ausgivingMessage>
  <div class="post-payment__bt-offline-content">
    <div class="post-payment__text-content post-payment__text-content--left">
      <p class="post-payment__text">
        {{ 'contribute.confirmation.ausgiving.message' | translateLang }}
      </p>
      <p
        class="post-payment__text"
        [innerHTML]="'contribute.confirmation.ausgiving.contact' | translateLang"
      ></p>
    </div>
    <h4 class="post-payment__section-heading">
      {{ 'contribute.confirmation.donation_details' | translateLang }}
    </h4>
    <div class="post-payment__details">
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.bank_name' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.bank_name_value' | translateLang
        }}</span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.bsb' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.bsb_value' | translateLang
        }}</span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.account' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.account_value' | translateLang
        }}</span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.legal_name' | translateLang
        }}</span>
        <span
          class="post-payment__value"
          [innerHTML]="'contribute.confirmation.ausgiving.legal_name_value' | translateLang"
        ></span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.business_address' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.business_address_value' | translateLang
        }}</span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.bank_address' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.bank_address_value' | translateLang
        }}</span>
      </div>
      <div class="post-payment__row">
        <span class="post-payment__label">{{
          'contribute.confirmation.ausgiving.swift' | translateLang
        }}</span>
        <span class="post-payment__value">{{
          'contribute.confirmation.ausgiving.swift_value' | translateLang
        }}</span>
      </div>
    </div>
    @if (paymentData.networkMemberLogoUrl) {
      <img
        class="post-payment__logo post-payment__logo--left"
        [src]="paymentData.networkMemberLogoUrl"
        [alt]="paymentData.networkMemberName || 'AusGiving'"
      />
    }
  </div>
</ng-template>

<!-- DAF Message Template -->
<ng-template #DafMessage>
  <div class="post-payment__text-content">
    @if (environmentName === environments.CNCF) {
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.daf' | translateLang: { name: tenantName }"
      ></p>
    } @else if (environmentName === environments.IGF) {
      <p
        class="post-payment__text"
        [innerHTML]="
          'offline.contribution.daf'
            | translateLang
              : {
                  fundName: paymentData.fundName || paymentData.entityName,
                  fundNumber: paymentData.fundNumber,
                  email: email,
                }
            | translateCut: 0
        "
      ></p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 1 }}
      </p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 2 }}
      </p>
      <p class="post-payment__text">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 3 }}
      </p>
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.daf' | translateLang: { email: email } | translateCut: 4"
      ></p>
    } @else if (environmentName === environments.TOL) {
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.daf' | translateLang | translateCut: 0"
      ></p>
      <p class="post-payment__text">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 1 }}
      </p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 2 }}
      </p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 3 }}
      </p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 4 }}
      </p>
      <p class="post-payment__text mb-0">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 5 }}
      </p>
      <p class="post-payment__text">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 6 }}
      </p>
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.daf' | translateLang: { email: email } | translateCut: 7"
      ></p>
      <p class="post-payment__text">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 8 }}
      </p>
      <p class="post-payment__text">
        {{ 'offline.contribution.daf' | translateLang | translateCut: 9 }}
      </p>
    } @else {
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.submitted' | translateLang: { name: tenantName }"
      ></p>
      <p class="post-payment__text">{{ 'email.sent' | translateLang }}</p>
      <app-wiring-instructions [wiringInstructions]="wiringInstructions"></app-wiring-instructions>
    }
  </div>
</ng-template>

<!-- Publicly Traded Securities Message Template -->
<ng-template #PTSMessage>
  @if (isPuertoRico) {
    @if (paymentData.taxDeduction) {
      <ng-container *ngTemplateOutlet="networkMember"></ng-container>
    } @else {
      <div class="post-payment__network-member">
        <p
          class="post-payment__text"
          [innerHTML]="
            paymentData.networkMemberText
              | translateLang
                : {
                    email: email,
                    country: paymentData.country,
                    networkMemberName: paymentData.networkMemberName,
                  }
              | translateCut: 0
          "
        ></p>
        <p
          class="post-payment__text"
          [innerHTML]="
            paymentData.networkMemberText | translateLang: { email: email } | translateCut: 1
          "
        ></p>

        @if (paymentData.networkMemberLogoUrl) {
          <img
            class="post-payment__logo"
            width="200"
            [src]="paymentData.networkMemberLogoUrl"
            [alt]="paymentData.networkMemberName || 'Network Member'"
          />
        }
      </div>
    }
  } @else {
    <div class="post-payment__text-content">
      <p class="post-payment__text">
        {{ 'offline.contribution.pts' | translateLang | translateCut: 0 }}
      </p>
      @if (environmentName !== environments.CNCF) {
        <p
          class="post-payment__text"
          [innerHTML]="
            'offline.contribution.pts' | translateLang: { email: email } | translateCut: 1
          "
        ></p>
        <p
          class="post-payment__text"
          [innerHTML]="
            'offline.contribution.pts' | translateLang: { email: email } | translateCut: 2
          "
        ></p>
        <p class="post-payment__text mb-0">
          {{ 'offline.contribution.pts' | translateLang | translateCut: 3 }}
        </p>
        <p class="post-payment__text">
          {{ 'offline.contribution.pts' | translateLang | translateCut: 4 }}
        </p>
      }
    </div>
  }
</ng-template>

<!-- Physical Check Message Template -->
<ng-template #physicalCheckMessage>
  @if (environmentName === environments.CNCF) {
    <div class="post-payment__text-content">
      <p
        class="post-payment__text"
        [innerHTML]="'offline.contribution.physical.check' | translateLang"
      ></p>
    </div>
  } @else {
    @if (paymentData.taxDeduction && environmentName !== environments.TOL) {
      <ng-container *ngTemplateOutlet="networkMember"></ng-container>
    } @else {
      <div class="post-payment__text-content">
        <p class="post-payment__text mb-0">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 0 }}
        </p>
        <p class="post-payment__text">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 1 }}
        </p>
        <p class="post-payment__text font-weight-bold mb-0">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 2 }}
        </p>
        <p class="post-payment__text font-weight-bold mb-0">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 3 }}
        </p>
        <p class="post-payment__text font-weight-bold">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 4 }}
        </p>
        <p class="post-payment__text">
          {{ 'offline.contribution.physical.check' | translateLang | translateCut: 5 }}
        </p>
        <p class="post-payment__text">{{ 'email.sent' | translateLang }}</p>
      </div>
    }
  }
</ng-template>

<!-- Other Assets Message Template -->
<ng-template #otherMessage>
  @if (
    !(
      environmentName === environments.SWISS &&
      paymentData.taxDeduction &&
      ((paymentData.countryName | uppercase) === countryNames.NEW_ZEALAND ||
        (paymentData.countryName | uppercase) === countryNames.NETHERLANDS ||
        (paymentData.countryName | uppercase) === countryNames.ROMANIA)
    )
  ) {
    <div class="post-payment__credit-card-confirmation">
      <h1 class="post-payment__credit-card-heading">
        {{ 'contribute.confirmation.thank_you' | translateLang }}
      </h1>
      <p
        class="post-payment__credit-card-message"
        [innerHTML]="
          'offline.contribution.other' | translateLang: { name: tenantName } | translateCut: 0
        "
      ></p>
      <p
        class="post-payment__credit-card-message"
        [innerHTML]="
          'offline.contribution.other' | translateLang: { email: email } | translateCut: 1
        "
      ></p>
    </div>
  } @else {
    <ng-container *ngTemplateOutlet="networkMember"></ng-container>
  }
</ng-template>

<!-- Network Member Template -->
<ng-template #networkMember>
  @if (loadingCountryDetails) {
    <app-loading-overlay [visible]="true" [indeterminate]="true"></app-loading-overlay>
  } @else {
    <div class="post-payment__network-member post-payment__network-member--left-aligned">
      @if (networkMemberRedirectUrl) {
        <p
          class="post-payment__text post-payment__text--large post-payment__text--left"
          [innerHTML]="
            'donate.contribution.confirmation.with.redirect'
              | translateLang
                : { country: paymentData.country, networkMemberName: paymentData.networkMemberName }
              | translateCut: 0
          "
        ></p>
        <p class="post-payment__text post-payment__text--large post-payment__text--left">
          <a
            [href]="networkMemberRedirectUrl"
            class="post-payment__link"
            target="_blank"
            rel="noopener noreferrer"
          >
            {{ 'donate.contribution.confirmation.with.redirect' | translateLang | translateCut: 1 }}
          </a>
          {{
            'donate.contribution.confirmation.with.redirect'
              | translateLang: { networkMemberName: paymentData.networkMemberName }
              | translateCut: 2
          }}
        </p>
        <div class="post-payment__warning">
          <svg
            class="post-payment__warning-icon"
            width="24"
            height="30"
            viewBox="0 0 24 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="15" r="10" stroke="#ffc609" stroke-width="2" fill="none" />
            <path d="M12 10v6m0 4h.01" stroke="#ffc609" stroke-width="2" stroke-linecap="round" />
          </svg>
          <p class="post-payment__warning-text">
            {{ 'donate.contribution.confirmation.with.redirect' | translateLang | translateCut: 3 }}
          </p>
        </div>
      } @else if (networkMemberSuccessMessage) {
        @if (networkMemberSuccessMessageIsRtf) {
          <!--
            A rich-text message carries its own block elements, so it goes in a div rather than
            the <p> the plain branch uses, and it is not split on `||` (the editor never emits
            that delimiter). The richText pipe sanitizes it against an explicit allow list;
            Angular's own [innerHTML] sanitizer would strip the inline colour, font size and
            alignment the admin chose.
          -->
          <!--
            Deliberately NOT post-payment__text--left: that modifier sets
            `text-align: left !important` on descendant p/span/b/strong, which would override
            the centre/right alignment the admin chose in the editor.
          -->
          <div
            class="post-payment__rich-text"
            [innerHTML]="networkMemberSuccessMessage | richText: networkMemberSuccessMessageFormat"
          ></div>
        } @else if (networkMemberSuccessMessageIsLegacySegmented) {
          <!--
            LEGACY, UNCHANGED. A `||`-delimited message predates the format toggle: it was written
            in the old raw textarea and rendered straight through [innerHTML], and its middle
            segment is an anchor by design (see `donate.contribution.confirmation.with.redirect`).
            Escaping it would turn that link into visible tag text, so this branch stays exactly as
            it was. Nothing produces `||` any more, so only existing configs reach it.
          -->

          <!-- Section 0: Thank you message (only shown if content exists) -->
          @if (networkMemberSuccessMessage | translateCut: 0) {
            <p
              class="post-payment__text post-payment__text--large post-payment__text--left"
              [innerHTML]="networkMemberSuccessMessage | translateCut: 0"
            ></p>
          }

          <!-- Section 1: Click here link message (only shown if content exists) -->
          @if (networkMemberSuccessMessage | translateCut: 1) {
            <p
              class="post-payment__text post-payment__text--large post-payment__text--left"
              [innerHTML]="networkMemberSuccessMessage | translateCut: 1"
            ></p>
          }

          <!-- Section 2: Warning box with warning message (only shown if content exists) -->
          @if (networkMemberSuccessMessage | translateCut: 2) {
            <div class="post-payment__warning">
              <svg
                class="post-payment__warning-icon"
                width="24"
                height="30"
                viewBox="0 0 24 30"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <circle cx="12" cy="15" r="10" stroke="#ffc609" stroke-width="2" fill="none" />
                <path
                  d="M12 10v6m0 4h.01"
                  stroke="#ffc609"
                  stroke-width="2"
                  stroke-linecap="round"
                />
              </svg>
              <p
                class="post-payment__warning-text"
                [innerHTML]="networkMemberSuccessMessage | translateCut: 2"
              ></p>
            </div>
          }
        } @else {
          <!--
            A plain message authored through the toggle. Escaped, so characters like `<` and `&`
            show literally instead of being parsed as markup and swallowing the rest of the line.
          -->
          <p
            class="post-payment__text post-payment__text--large post-payment__text--left"
            [innerHTML]="networkMemberSuccessMessage | richText: SuccessMessageFormat.PLAIN"
          ></p>
        }
      } @else {
        <p
          class="post-payment__text post-payment__text--large post-payment__text--left"
          [innerHTML]="
            paymentData.networkMemberText
              | translateLang
                : {
                    email: email,
                    country: paymentData.country,
                    networkMemberName: paymentData.networkMemberName,
                  }
              | translateCut: 0
          "
        ></p>
        <p
          class="post-payment__text post-payment__text--large post-payment__text--left"
          [innerHTML]="
            paymentData.networkMemberText | translateLang: { email: email } | translateCut: 1
          "
        ></p>
        <div class="post-payment__warning">
          <svg
            class="post-payment__warning-icon"
            width="24"
            height="30"
            viewBox="0 0 24 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle cx="12" cy="15" r="10" stroke="#ffc609" stroke-width="2" fill="none" />
            <path d="M12 10v6m0 4h.01" stroke="#ffc609" stroke-width="2" stroke-linecap="round" />
          </svg>
          <p class="post-payment__warning-text">
            {{ 'donate.contribution.confirmation.with.redirect' | translateLang | translateCut: 3 }}
          </p>
        </div>
      }
      @if (paymentData.networkMemberLogoUrl) {
        <img
          class="post-payment__logo post-payment__logo--left"
          [src]="paymentData.networkMemberLogoUrl"
          [alt]="paymentData.networkMemberName || 'Network Member'"
        />
      }
    </div>
  }
</ng-template>

<!-- New Credit Card Confirmation Template -->
<ng-template #newCreditCardConfirmation>
  <div class="post-payment__credit-card-confirmation">
    <!-- Thank You Heading -->
    <h1 class="post-payment__credit-card-heading">
      {{ 'contribute.confirmation.credit_card.thank_you' | translateLang }}
    </h1>

    <!-- Payment Processing Message -->
    <p class="post-payment__credit-card-message">
      @if (paymentData.recurrencePattern) {
        @if (paymentData.wasLoggedIn) {
          {{ 'contribute.confirmation.credit_card.recurring.private' | translateLang }}
        } @else {
          {{ 'contribute.confirmation.credit_card.recurring.public' | translateLang }}
        }
      } @else {
        {{ 'contribute.confirmation.credit_card.payment_processing' | translateLang }}
      }
    </p>

    <!-- Summary Section -->
    @if (displaySummaryPage()) {
      <div class="post-payment__credit-card-summary">
        <h2 class="post-payment__credit-card-summary-heading">
          {{ 'contribute.confirmation.credit_card.summary_title' | translateLang }}
        </h2>

        <div class="post-payment__credit-card-row">
          <span class="post-payment__credit-card-label">{{ 'general.fund' | translateLang }}</span>
          <span class="post-payment__credit-card-value">
            {{
              paymentData.recurrencePattern
                ? paymentData.fundName || paymentData.entityName
                : paymentData.entityName
            }}
          </span>
        </div>

        <div class="post-payment__credit-card-row">
          <span class="post-payment__credit-card-label">{{
            'summary.amount' | translateLang
          }}</span>
          <span class="post-payment__credit-card-value">
            {{ paymentData.currencySymbol }}
            {{ paymentData.amount | number: '1.2-2' | numberFormat }}
          </span>
        </div>

        @if (paymentData.paymentId && !paymentData.recurrencePattern) {
          <div class="post-payment__credit-card-row">
            <span class="post-payment__credit-card-label">{{
              'summary.payment.id' | translateLang
            }}</span>
            <span class="post-payment__credit-card-value">{{ paymentData.paymentId }}</span>
          </div>
        }
        @if (paymentData.subscriptionId && paymentData.recurrencePattern) {
          <div class="post-payment__credit-card-row">
            <span class="post-payment__credit-card-label">{{
              'summary.subscription.id' | translateLang
            }}</span>
            <span class="post-payment__credit-card-value">{{ paymentData.subscriptionId }}</span>
          </div>
        }
      </div>
    }
  </div>
</ng-template>

<!-- Loading Template -->
<ng-template #stillLoading>
  <app-loading-overlay [visible]="true" [indeterminate]="true"></app-loading-overlay>
</ng-template>

<!-- Portal toast container -->
<app-toast />
